import { Component, HostListener, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CouponErrorMessages } from 'src/app/constants/ErrorMessages';
import { CouponLabelConstants } from 'src/app/constants/LabelConstants';
import { ApiUrlHelper } from 'src/app/common/api-url-helper';
import { CommonService } from 'src/app/services/common/common.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { DirtyComponent } from 'src/app/component/dirty/dirty.component';
import { CouponPassType, DiscountType } from 'src/app/models/commonModel';

@Component({
    selector: 'app-add-edit-coupon-dialog',
    templateUrl: './add-edit-coupon-dialog.component.html',
    styleUrls: ['./add-edit-coupon-dialog.component.scss'],
    standalone: false
})
export class AddEditCouponDialogComponent {

  // Common Properties
  maxPriceLength: number = 8; // Adjust this value as per your requirement
  maxPercentageLength: number = 5; // Adjust this value as per your requirement
  CouponId: any;
  CouponForm: FormGroup;
  isEditing: boolean;
  imageSrc: string;
  EditedCouponDetails: any;
  submitted: boolean = false;
  discountTypes: any;
  selectedDiscountType: any;
  AutoGeneratedCouponCode: any;
  UserId: any;
  View: boolean;
  ErrorMessages: any;
  IsPreviewTrue: boolean = false;
  IsSelectedDateValid: boolean = false;
  @ViewChild('startDate') startDate!: MatDatepicker<Date>;
  @ViewChild('endDate') endDate!: MatDatepicker<Date>;
  LabelConstants: any;
  CouponTypeEnum: any;
  currentDate = new Date();
  decryptUserId:any;

  constructor(
    private dialogRef: MatDialogRef<AddEditCouponDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private api: ApiUrlHelper,
    private commonService: CommonService,
    private fb: FormBuilder,
    private storageService: StorageService
  ) {
    this.View = data.View;
    this.isEditing = data.IsEdit;
    this.CouponId = data.CouponId;
    this.IsPreviewTrue = data.ShowPreview;
    this.CouponTypeEnum = CouponPassType;
    this.selectedDiscountType = data.selectedDiscountType;
    if (this.isEditing || this.CouponId > 0 || this.IsPreviewTrue) {
      Number.parseInt(this.CouponId);
      this.fetchCouponDetails();
    }
  }

  ngOnInit(): void {
    this.LabelConstants = CouponLabelConstants;
    this.discountTypes = DiscountType;
    this.CouponForm = this.fb.group({
      CouponCode: ['', Validators.required],
      CouponDescription: [''],
      StartDate: ['', Validators.required],
      EndDate: ['', Validators.required],
      DiscountType: [this.discountTypes.Flat, Validators.required],
      Price: ['', Validators.required],
      CouponType: ['', Validators.required],
    });
    this.UserId = this.storageService.getValue('userId');
    if (this.UserId != null && this.UserId != undefined && this.UserId != '' && this.UserId != 'undefined') {
      this.decryptUserId = this.commonService.Decrypt(this.UserId);
      this.UserId = this.decryptUserId;
    }
    Number.parseInt(this.UserId);
    this.ErrorMessages = CouponErrorMessages;
    if (this.CouponId === 0) {
      this.selectedDiscountType = this.discountTypes.Flat;
    }
  }

  get GetCouponFormConntrols() {
    return this.CouponForm.controls;
  }

  cancel() {
    if (this.CouponForm.dirty) {
      const dialogRef = this.dialog.open(DirtyComponent, {
        width: '500px',
        data: {
          action: 'Coupon',
        },
        disableClose: true,
        autoFocus: false
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  public canDeactivated(event: BeforeUnloadEvent): void {
    if (this.CouponForm.dirty) {
      event.preventDefault();
      event.returnValue = false;
    }
  }

  openDatepicker() {
    this.startDate.open();
  }

  openEndDatePicker() {
    this.endDate.open();
  }

  getMaxPriceLength(): number {
    return this.selectedDiscountType === this.discountTypes.Percentage ? 3 : 6;
  }

  getDialogTitle() {
    if (this.IsPreviewTrue) {
      return 'Coupon Preview';
    }
    return this.isEditing ? 'Edit Coupon' : 'Add Coupon';
  }

  submitData() {
    this.submitted = true;
    if (this.CouponForm.value.Price <= 0 && this.CouponForm.value.DiscountType == this.discountTypes.Flat) {
      this.toastr.error(this.ErrorMessages.PriceValid);
      return;
    }
    if (this.CouponForm.value.Price <= 0 && this.CouponForm.value.DiscountType == this.discountTypes.Percentage) {
      this.toastr.error(this.ErrorMessages.PercentageValid);
      return;
    }
    this.CouponForm.get('CouponCode')?.enable();
    let api = this.api.apiUrl.Coupon.InsertUpdateCoupon;
    let CouponModel = {
      CouponCodeId: 0 | this.CouponId,
      CouponDescription: this.CouponForm.value.CouponDescription?.trim(),
      CouponCode: this.CouponForm.value.CouponCode,
      StartDate: this.convertToIST(this.CouponForm.value.StartDate),
      EndDate: this.convertToIST(this.CouponForm.value.EndDate),
      DiscountType: this.CouponForm.value.DiscountType,
      Discount: this.CouponForm.value.Price,
      UserId: this.UserId,
      CouponType: this.CouponForm.value.CouponType,
    };
    this.spinner.show();
    this.commonService.doPost(api, CouponModel).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.spinner.hide();
          this.toastr.success(response.message);
          this.dialogRef.close();
          this.dialogRef.close(true);
        } else {
          this.spinner.hide();
          this.toastr.error(response.message);
        }
      },
      error(err) {
        this.spinner.hide();
        console.log(err);
      },
    });
  }

  // If User Editing Then Patch The Form Value
  fetchCouponDetails() {
    this.spinner.show();
    let api = this.api.apiUrl.Coupon.GetCouponById.replace('{Id}', this.CouponId);
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        this.EditedCouponDetails = response.data;
        this.CouponForm.patchValue({
          CouponCode: this.EditedCouponDetails.couponCode,
          CouponDescription: this.EditedCouponDetails.couponDescription,
          StartDate: this.EditedCouponDetails.startDate,
          EndDate: this.EditedCouponDetails.endDate,
          DiscountType: this.EditedCouponDetails.discountType,
          Price: this.EditedCouponDetails.discount.toFixed(2),
          CouponType: this.EditedCouponDetails.couponType,
        });
        this.CouponForm.get('CouponCode')?.disable();
        if (this.IsPreviewTrue) {
          this.CouponForm.disable();
        }
        this.spinner.hide();
      },
      error(err) {
        this.spinner.hide();
        console.log(err);
      },
    });
  }

  validateCouponCodeDate() {
    const startDate = new Date(this.CouponForm.value.StartDate);
    const endDate = new Date(this.CouponForm.value.EndDate);
    if (endDate >= startDate) {
      this.CouponForm.get('EndDate')?.setErrors(null);
      this.IsSelectedDateValid = false;
    } else {
      this.CouponForm.get('EndDate')?.setErrors({ 'invalidEndDate': true });
      this.IsSelectedDateValid = true;
    }
  }

  convertToIST(utcTime: string): any {
    if (utcTime == null) {
      return null;
    }
    const utcDateTime = new Date(utcTime);
    const istDateTime = new Date(
      utcDateTime.getTime() + (5 * 60 + 30) * 60 * 1000
    );
    return istDateTime.toISOString();
  }

  updateSelectedDiscountType(event: any) {
    this.selectedDiscountType = event.value;
    const priceControl = this.CouponForm.get('Price');
    priceControl.setValue('');
  }

  generateCouponCode() {
    this.AutoGeneratedCouponCode = this.commonService.generateCouponCode();
    this.CouponForm.patchValue({
      CouponCode: this.AutoGeneratedCouponCode,
    });
  }
}
